import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "@phosphor-icons/react";
import { PHONE_DISPLAY, PHONE_TEL, IMAGES } from "@/lib/brand";

const LINES = ["Fast, Damage-Free", "Towing in", "Rock Hill."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] overflow-hidden"
    >
      {/* LEFT */}
      <div className="relative z-20 flex flex-col justify-center px-5 md:px-10 lg:pl-16 lg:pr-10 pt-28 pb-16 lg:py-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-[var(--tt-green)] mb-6 flex items-center gap-3"
        >
          <span className="h-2 w-2 rounded-full bg-[var(--tt-green)] tt-green-glow animate-pulse" />
          Open Mon–Sat · 8AM–6PM · Rock Hill, SC
        </motion.div>

        <h1 className="font-display uppercase text-[2.9rem] leading-[0.92] sm:text-6xl lg:text-[5.2rem] xl:text-[6rem] tracking-tighter">
          {LINES.map((line, i) => (
            <span key={line} className="tt-line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 0 ? "text-[var(--tt-green)] tt-text-glow" : "text-white"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-7 max-w-md text-base md:text-lg text-zinc-400 leading-relaxed"
          data-testid="hero-subheadline"
        >
          No call centers. No dispatchers. You speak{" "}
          <span className="text-white font-semibold">directly with the owner</span> — the same
          person who shows up to get you off the road.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
        >
          <a
            href={PHONE_TEL}
            data-testid="hero-dispatch-button"
            className="group flex items-center justify-center gap-3 bg-[var(--tt-green)] text-black font-display uppercase text-xl md:text-2xl px-9 py-5 border-2 border-[var(--tt-green)] hover:bg-transparent hover:text-[var(--tt-green)] hover:shadow-[0_0_34px_rgba(57,255,20,0.5)] transition-[background-color,color,box-shadow] duration-300 tt-clip-card"
          >
            <Phone weight="fill" className="h-6 w-6 group-hover:rotate-12 transition-transform" />
            Dispatch a Truck Now
          </a>
          <a
            href="#dispatch"
            data-testid="hero-request-link"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("dispatch");
              if (window.__lenis) {
                window.__lenis.scrollTo(el, { offset: -80 });
              } else if (el) {
                el.scrollIntoView({ behavior: "smooth" });
              }
            }}
            className="flex items-center justify-center gap-2 font-mono text-sm uppercase tracking-widest text-zinc-300 hover:text-[var(--tt-green)] transition-colors px-2 cursor-pointer"
          >
            Or request online →
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="hidden lg:flex items-center gap-2 mt-16 text-zinc-600 font-mono text-xs uppercase tracking-[0.2em]"
        >
          <ArrowDown className="h-4 w-4 animate-bounce" /> Scroll
        </motion.div>
      </div>

      {/* RIGHT — clipped image with parallax + green spotlight */}
      <div className="relative min-h-[55vh] lg:min-h-full lg:tt-clip-hero overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src={IMAGES.heroTruck}
            alt="Modern black flatbed tow truck on a wet Rock Hill street at night"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--tt-black)] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--tt-black)] via-transparent to-transparent" />
        <div
          className="absolute inset-0 mix-blend-color opacity-25"
          style={{ background: "radial-gradient(60% 60% at 60% 40%, var(--tt-green), transparent)" }}
        />
        <div className="absolute bottom-6 right-6 z-20 font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/60 border border-white/15 backdrop-blur-md px-3 py-2">
          Flatbed · Low-Clearance · Damage-Free
        </div>
      </div>
    </section>
  );
};
